import { formatDate, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
  DialogService,
  DIALOG_RETURN_TYPES,
  DialogComponent,
} from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrDatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Store } from '@ngrx/store';
import CycleUtils from 'src/app/cycle/cycle-utils';
import { Cycle, ObservationSettingEnum, Section } from 'src/app/cycle/models/cycle.model';
import Utils from 'src/app/utils';

import {
  MortalityRegistration,
  ObservationsRegistration,
  TabMark,
} from '../../models/performance.model';
import * as performanceActions from './../../store/actions/performance.actions';

import { ObservationsRegistrationFormComponent } from './observations-registration-form.component';

export interface ObservationsRegistrationDialogInputData extends AbstractDialogData {
  selectedSection?: Section;
  sections?: Section[];
  cycle: Cycle;
  observations: ObservationsRegistration[];
  weekNr: number;
}

export interface ObservationsRegistrationDialogResultData {
  observations: ObservationsRegistration[];
}

@Component({
  selector: 'app-observations-registration',
  templateUrl: './observations-registration.component.html',
  styleUrls: ['./observations-registration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogComponent,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    FormsModule,
    NgFor,
    MatOption,
    NgIf,
    MatTabGroup,
    MatTab,
    ObservationsRegistrationFormComponent,
    MatIcon,
    TranslatePipe,
    AgrDatePipe,
    AgrNumberPipe,
  ],
})
export class ObservationsRegistrationComponent extends AbstractDialog<ObservationsRegistrationDialogResultData> {
  sectionStartDate: Date;
  minDay: number;
  previousSectionId: number;
  numberOfAnimalsStartOfSection: number;
  numberOfAnimalsStartOfWeek: number;
  maxDay: number;
  tabs: Array<{ name: string; showFields?: string[]; day: number; chickAgeInDays?: number }> = [];
  formGroups: FormGroup[] = [];
  sections: Section[];
  selectedSectionId: number;
  selectedSection: Section;
  formGroupsValueChanged = false;
  allSettingsAreOff: boolean;
  oneDayTime = 1000 * 60 * 60 * 24;
  activeObservations: ObservationsRegistration[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ObservationsRegistrationDialogInputData,
    protected mdDialogRef: MatDialogRef<
      ObservationsRegistrationComponent,
      AbstractDialogResultData<ObservationsRegistrationDialogResultData>
    >,
    private readonly locale: LocaleService,
    private readonly formBuilder: FormBuilder,
    private readonly dialogService: DialogService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly store: Store<RootState>,
  ) {
    super();
    // using default settings if they are not set in the cycle (which is possible)
    if (!this.data.cycle.observationSettings) {
      this.data.cycle = {
        ...this.data.cycle,
        observationSettings: {
          animalWeightInGrams: ObservationSettingEnum.Off,
          uniformity: ObservationSettingEnum.Off,
          coefficientOfVariation: ObservationSettingEnum.Off,
          feedName: ObservationSettingEnum.Off,
          feedIntakeInGramsPerAnimal: ObservationSettingEnum.Off,
          mortality: ObservationSettingEnum.Off,
          waterIntakeInLitersTotal: ObservationSettingEnum.Off,
          numberOfMinutesLight: ObservationSettingEnum.Off,
          lightIntensityInLux: ObservationSettingEnum.Off,
          notes: ObservationSettingEnum.Off,
        },
      };
    }

    // check if all settings are off, because then we show a message to the user
    this.allSettingsAreOff =
      !this.data.cycle.observationSettings || this.getShowFields().length === 0;

    // get unique barns for selector and pre-select first one if any
    if (!!this.data.sections && !!this.data.selectedSection) {
      this.sections = this.getSectionsWithAllocationsOrMovements();
      this.selectedSectionId = this.data.selectedSection.id;
    } else {
      this.sections = this.getSectionsWithAllocationsOrMovements();
      this.selectedSectionId = this.sections[0].id;
    }

    this.setSelectedSection(this.selectedSectionId);
  }

  getSectionsWithAllocationsOrMovements() {
    const sectionsWithAllocationsOrMovements: Section[] = [];
    this.data.cycle.arrivals.forEach((arrival) => {
      arrival.allocations.forEach((allocation) => {
        if (!sectionsWithAllocationsOrMovements.find((a) => a.id === allocation.section.id)) {
          sectionsWithAllocationsOrMovements.push(allocation.section);
        }
      });
    });

    if (this.data.cycle.movements) {
      this.data.cycle.movements.forEach((movement) => {
        if (!sectionsWithAllocationsOrMovements.find((a) => a.id === movement.toSection.id)) {
          sectionsWithAllocationsOrMovements.push(movement.toSection);
        }
      });
    }

    return sectionsWithAllocationsOrMovements;
  }

  getShowFields(): string[] {
    const inputDefinitionKeys = Object.keys(this.data.cycle.observationSettings);
    return inputDefinitionKeys.filter((key: string) => {
      const foundValue = (
        (<unknown>this.data.cycle.observationSettings) as {
          [key: string]: string;
        }
      )[key];
      return foundValue !== ObservationSettingEnum.Off.toString();
    });
  }

  private isObservationComplete(observation: ObservationsRegistration) {
    return (
      observation.animalWeightInGrams !== undefined ||
      observation.uniformity !== undefined ||
      observation.coefficientOfVariation !== undefined ||
      observation.feedName !== undefined ||
      observation.feedIntakeInGramsPerAnimal !== undefined ||
      observation.waterIntakeInLitersTotal !== undefined ||
      observation.mortalityRegistration?.death !== undefined ||
      observation.mortalityRegistration?.cripple !== undefined ||
      observation.mortalityRegistration?.sexError !== undefined ||
      observation.mortalityRegistration?.selection !== undefined ||
      observation.mortalityRegistration?.other !== undefined ||
      observation.mortalityRegistration?.damages !== undefined ||
      observation.numberOfMinutesLight !== undefined ||
      observation.lightIntensityInLux !== undefined ||
      observation.notes !== undefined
    );
  }

  private createTabs() {
    if (this.activeObservations && this.activeObservations.length > 0) {
      this.tabs = [];
      this.activeObservations.forEach((observation, i) => {
        this.tabs.push({
          name: '',
          showFields: this.getShowFields(),
          day: observation.day,
          chickAgeInDays: this.getChickAgeInDays(observation),
        });
        this.markTab(
          i,
          observation.day,
          this.isObservationComplete(observation) ? TabMark.Finished : TabMark.Clean,
        );
      });
      this.formGroups = [];
      for (let tab = 0; tab < this.tabs.length; tab++) {
        this.createFormGroup(this.activeObservations[tab]);
      }
    }
  }

  private createFormGroup(data: ObservationsRegistration) {
    this.formGroups.push(
      this.formBuilder.group({
        animalWeightInGrams: [data?.animalWeightInGrams],
        uniformity: [data?.uniformity],
        coefficientOfVariation: [data?.coefficientOfVariation],
        feedName: [data?.feedName],
        feedIntakeInGramsPerAnimal: [data?.feedIntakeInGramsPerAnimal],
        waterIntakeInLitersTotal: [data?.waterIntakeInLitersTotal],
        mortalityDeath: [data?.mortalityRegistration?.death],
        mortalityCripple: [data?.mortalityRegistration?.cripple],
        mortalitySexError: [data?.mortalityRegistration?.sexError],
        mortalitySelection: [data?.mortalityRegistration?.selection],
        mortalityOther: [data?.mortalityRegistration?.other],
        mortalityDamages: [data?.mortalityRegistration?.damages],
        numberOfMinutesLight: [
          data?.numberOfMinutesLight ? data?.numberOfMinutesLight / 60 : undefined,
        ],
        lightIntensityInLux: [data?.lightIntensityInLux],
        notes: [data?.notes],
      }),
    );
    this.formGroups.forEach((formgroup, index) => {
      formgroup.valueChanges.subscribe((_) => {
        this.formGroupsValueChanged = true;
        this.formGroupWasChanged(index);
      });
    });
  }

  formGroupWasChanged(index: number) {
    this.markTab(index, this.tabs[index].day, TabMark.PendingChanges);
  }

  getCleanTabName(day: number): string {
    return formatDate(
      CycleUtils.getSectionDateOfDayInCycle(this.data.cycle, day, this.selectedSectionId),
      'd MMM yy',
      this.locale.getLocale(),
    );
  }

  markTab(tabIndex: number, day: number, tabMark: TabMark) {
    let mark = '';
    switch (tabMark) {
      case TabMark.Finished:
        mark = `${
          new DOMParser().parseFromString('&#10003;', 'text/html').documentElement.textContent
        } `;
        break;
      case TabMark.PendingChanges:
        mark = '* ';
        break;
    }
    this.tabs[tabIndex].name = `${mark}${this.getCleanTabName(day)}`;
  }

  sectionSelectChange($event: MatSelectChange) {
    if (this.formGroupsValueChanged) {
      setTimeout(() => {
        this.selectedSectionId = this.previousSectionId;
        this.changeDetectorRef.detectChanges();
      }, 100);
      this.dialogService
        .confirm({
          title: this.locale.translate('observations-labels.unsaved-changes-title'),
          text: this.locale.translate('observations-labels.unsaved-changes-description'),
          confirmButtonText: this.locale.translate('observations-labels.unsaved-changes-confirm'),
          cancelButtonText: this.locale.translate('observations-labels.unsaved-changes-cancel'),
          pushHistoryState: false,
        })
        .subscribe((result) => {
          if (result.status !== DIALOG_RETURN_TYPES.confirm) {
            return;
          } else {
            setTimeout(() => {
              this.selectedSectionId = $event.value;
              this.setSelectedSection($event.value);
              this.changeDetectorRef.detectChanges();
            }, 100);
          }
        });
    } else {
      this.selectedSectionId = $event.value;

      this.setSelectedSection($event.value);
    }
  }

  setSelectedSection(sectionId: number) {
    this.formGroupsValueChanged = false;
    this.previousSectionId = sectionId;
    this.selectedSection = this.sections.find((s) => s.id === sectionId);
    this.minDay = this.data.weekNr * 7 - 7;
    this.maxDay = this.data.weekNr * 7 - 1;

    const sectionStartDateString = CycleUtils.getSectionStartDate(this.data.cycle, sectionId);

    if (sectionStartDateString) {
      this.sectionStartDate = Utils.toUtcDate(
        new Date(CycleUtils.getSectionStartDate(this.data.cycle, sectionId)),
      );
    } else {
      this.sectionStartDate = undefined;
    }

    if (this.sectionStartDate) {
      this.numberOfAnimalsStartOfSection = CycleUtils.getNumberOfAnimalsInSectionAtDate(
        this.data.cycle,
        sectionId,
        this.sectionStartDate,
        [],
        true,
        null,
      );
    }

    this.numberOfAnimalsStartOfWeek = CycleUtils.getNumberOfAnimalsInSectionAtDate(
      this.data.cycle,
      sectionId,
      CycleUtils.getSectionDateOfDayInCycle(this.data.cycle, this.minDay, sectionId),
      this.data.observations.filter((o) => o.day < this.minDay),
      true,
      null,
    );

    this.activeObservations = this.data.observations
      .filter((o) => o.sectionId === sectionId && o.day >= this.minDay && o.day <= this.maxDay)
      .map((o) => ({
        ...o,
      }))
      .sort((a, b) => a.day - b.day);

    if (this.activeObservations?.length !== 7) {
      const observations: ObservationsRegistration[] = [];
      for (let i = this.minDay; i <= this.maxDay; i++) {
        const index = this.activeObservations.findIndex((o) => o.day === i);

        if (index === -1) {
          observations.push({
            barnId: this.selectedSection.barn.id,
            cycleId: this.data.cycle.id,
            day: i,
            sectionId: this.selectedSectionId,
          });
        } else {
          observations.push(this.activeObservations[index]);
        }
      }

      this.activeObservations = [...observations];
    }

    this.setTitle();

    this.createTabs();
  }

  setTitle() {
    this.data.title = `${this.locale.translate('performance.cycle')} ${
      this.data.cycle.cycleNumber
    } - ${this.locale.translate('performance.week')} ${this.data.weekNr} - ${this.locale.translate(
      'observations-labels.select-section',
    )} ${this.selectedSection.name}`;
  }

  getFirstDayOfWeek() {
    return CycleUtils.getSectionDateOfDayInCycle(
      this.data.cycle,
      this.minDay,
      this.selectedSectionId,
    );
  }

  getLastDayOfWeek() {
    return CycleUtils.getSectionDateOfDayInCycle(
      this.data.cycle,
      this.maxDay,
      this.selectedSectionId,
    );
  }

  private getDataFromForms() {
    this.activeObservations.forEach((observation, i) => {
      const formGroup = this.formGroups[i];
      observation.animalWeightInGrams = formGroup.controls?.animalWeightInGrams?.value;
      observation.uniformity = formGroup.controls?.uniformity?.value;
      observation.coefficientOfVariation = formGroup.controls?.coefficientOfVariation?.value;
      observation.feedName = formGroup.controls?.feedName?.value;
      observation.feedIntakeInGramsPerAnimal =
        formGroup.controls?.feedIntakeInGramsPerAnimal?.value;
      observation.waterIntakeInLitersTotal = formGroup.controls?.waterIntakeInLitersTotal?.value;
      observation.numberOfMinutesLight = formGroup.controls?.numberOfMinutesLight?.value
        ? formGroup.controls?.numberOfMinutesLight?.value * 60
        : undefined;
      observation.lightIntensityInLux = formGroup.controls?.lightIntensityInLux?.value;
      observation.notes = formGroup.controls?.notes?.value;
      observation.mortalityRegistration = {
        death: formGroup.controls?.mortalityDeath?.value,
        cripple: formGroup.controls?.mortalityCripple?.value,
        sexError: formGroup.controls?.mortalitySexError?.value,
        selection: formGroup.controls?.mortalitySelection?.value,
        other: formGroup.controls?.mortalityOther?.value,
        damages: formGroup.controls?.mortalityDamages?.value,
      };
    });
  }

  sumMortality(mortalityRegistration: MortalityRegistration): number {
    return (
      (mortalityRegistration.cripple ?? 0) +
      (mortalityRegistration.death ?? 0) +
      (mortalityRegistration.other ?? 0) +
      (mortalityRegistration.selection ?? 0) +
      (mortalityRegistration.sexError ?? 0) +
      (mortalityRegistration.damages ?? 0)
    );
  }

  save(close: boolean): boolean {
    this.getDataFromForms();

    const mortalityOverFlow = this.activeObservations.some((ao1) => {
      const observations = this.activeObservations.filter((ao2) => ao2.day <= ao1.day);

      const registeredMortality = observations.reduce(
        (acc: number, observation: ObservationsRegistration) =>
          acc + this.sumMortality(observation.mortalityRegistration),
        0,
      );

      const numberOfAnimals = CycleUtils.getNumberOfAnimalsInSectionAtDate(
        this.data.cycle,
        this.selectedSectionId,
        CycleUtils.getSectionDateOfDayInCycle(this.data.cycle, ao1.day, this.selectedSectionId),
        this.data.observations.filter((o) => o.day < this.minDay),
        true,
        null,
      );

      return registeredMortality > numberOfAnimals;
    });

    if (!mortalityOverFlow) {
      this.store.dispatch(
        performanceActions.upsertbservationsForCycleRequest({
          observations: this.activeObservations,
        }),
      );
      this.data.observations = [
        ...this.data.observations.filter(
          (so) =>
            this.activeObservations.findIndex(
              (o) => o.day === so.day && o.sectionId === so.sectionId && o.cycleId === so.cycleId,
            ) < 0,
        ),
      ]
        .concat(this.activeObservations)
        .sort((o) => o.day);
      this.activeObservations = this.activeObservations.map((o) => ({ ...o }));
      this.tabs.forEach((tab, i) => this.markTab(i, tab.day, TabMark.Finished));
      this.formGroupsValueChanged = false;
      if (close) {
        this.confirm({ observations: this.activeObservations });
      }
      return true;
    } else {
      // More animals than the available amount died.
      this.store.dispatch(
        NotificationActions.errorSnackbar({
          message: this.locale.translate('performance.mortality-overflow-error'),
        }),
      );
      return false;
    }
  }

  saveAndNextSection() {
    const saveSuccessful = this.save(false);
    if (saveSuccessful) {
      let sectionIndex = this.data.sections?.findIndex((o) => o.id === this.selectedSectionId);
      if (sectionIndex >= this.data.sections.length - 1) {
        sectionIndex = 0;
      } else {
        sectionIndex++;
      }

      this.selectedSectionId = this.data.sections[sectionIndex].id;

      this.setSelectedSection(this.selectedSectionId);
      this.changeDetectorRef.detectChanges();
    }
  }

  copyValuesFromDay(index: number, registration: ObservationsRegistration) {
    this.formGroups.forEach((formGroup, i) => {
      if (i !== index) {
        formGroup.controls?.feedName?.setValue(registration.feedName);
        formGroup.controls?.feedIntakeInGramsPerAnimal?.setValue(
          registration.feedIntakeInGramsPerAnimal,
        );
        formGroup.controls?.waterIntakeInLitersTotal?.setValue(
          registration.waterIntakeInLitersTotal,
        );
        formGroup.controls?.numberOfMinutesLight?.setValue(registration.numberOfMinutesLight);
        formGroup.controls?.lightIntensityInLux?.setValue(registration.lightIntensityInLux);
      }
    });
    this.store.dispatch(
      NotificationActions.successSnackbar({
        message: this.locale.translate('observations-labels.copy-values-successful'),
      }),
    );
  }

  getChickAgeInDays(observationsRegistration: ObservationsRegistration): number {
    const youngestChickBirthDate = CycleUtils.getYoungestChickBirthDateInSection(
      this.data.cycle,
      observationsRegistration.sectionId,
    );

    const registrationDate = CycleUtils.getSectionDateOfDayInCycle(
      this.data.cycle,
      observationsRegistration.day,
      observationsRegistration.sectionId,
    );

    if (youngestChickBirthDate && registrationDate) {
      return CycleUtils.getDaysDifferenceBetweenTwoDates(youngestChickBirthDate, registrationDate);
    }

    return undefined;
  }
}
